/**
 * Schemas for JSON that crosses a trust boundary into the calculator: localStorage, `?s=` share
 * links, settings-markdown imports and `Story_JSON__c` read back from Salesforce.
 *
 * `StorySnapshot` and `SharePayload` are large hand-written types that tolerate archived data
 * (legacy buckets, missing per-lever factors), so their nested fields are not re-derived here.
 * Instead each guard checks exactly the fields the readers (`migrateSnapshot`,
 * `payloadToStatePatch`) dereference without a fallback, and nothing else: an archived row that
 * only lacks fields still passes, a row whose `ucs` is not a list of objects does not. The
 * remaining fields are asserted by the `z.custom<T>` type, not checked at runtime.
 */
import { z } from 'zod';
import type { SharePayload, StorySnapshot } from '../types';

const looseRecord = z.record(z.string(), z.unknown());

/** What `migrateSnapshot()` reads: `roi.bucketNet`, `roi.parts[].buckets`, `ucs[].bucket`. */
const SnapshotReadsSchema = z.looseObject({
  roi: z.looseObject({ parts: z.array(z.looseObject({})).nullish() }).nullish(),
  ucs: z.array(z.looseObject({ bucket: z.string().nullish() })).nullish(),
});

/** What `payloadToStatePatch()` spreads, maps or indexes. Every key is optional. */
const SharePayloadReadsSchema = z.looseObject({
  p: looseRecord.nullish(),
  a: looseRecord.nullish(),
  s: looseRecord.nullish(),
  i: looseRecord.nullish(),
  tl: looseRecord.nullish(),
  ct: z.array(z.looseObject({})).nullish(),
  cu: z.array(z.looseObject({})).nullish(),
  rzm: looseRecord.nullish(),
  atm: looseRecord.nullish(),
});

export const StorySnapshotSchema = z.custom<StorySnapshot>(
  (value) => SnapshotReadsSchema.safeParse(value).success,
  'Not a story snapshot',
);

export const SharePayloadSchema = z.custom<SharePayload>(
  (value) => SharePayloadReadsSchema.safeParse(value).success,
  'Not a share payload',
);

export const BmRowSchema = z.object({
  id: z.string(),
  customer: z.string(),
  metricId: z.string(),
  metricText: z.string(),
  pre: z.string(),
  post: z.string(),
  imp: z.string(),
  notes: z.string(),
});
export type BmRow = z.infer<typeof BmRowSchema>;
